import { Pagination, SuccessResponse } from "common/common.interface";
import { useAppSelector, useSnack } from "common/hooks";
import { useInfiniteQueryWithLoading } from "common/hooks/src/api/useInfiniteQueryWithLoading";
import { useMutationWithLoading } from "common/hooks/src/api/useMutationWithLoading";
import { useQueryWithLoading } from "common/hooks/src/api/useQueryWithLoading";
import {
  Commande,
  CreateCommandePayload,
  DeleteDocumentPayload,
} from "modules/commande/types/commande.interface";
import {
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQueryClient,
} from "react-query";
import { useNavigate } from "react-router-dom";
import { COMMANDE_ROUTES } from "./commande.api.enum";
import axios from "axios";
import { getCommandeBackend } from "./backends";

export const useGetCommandes = (
  expired: boolean,
  searchName: string | undefined
): UseInfiniteQueryResult<Pagination<Commande>> => {
  const setSnack = useSnack();

  const getCommandes = async ({
    pageParam = 1,
  }): Promise<Pagination<Commande>> => {
    const commandeBackend = await getCommandeBackend();
    return await commandeBackend.getAllCommandes(pageParam, 10, expired, searchName);
  };

  return useInfiniteQueryWithLoading(
    COMMANDE_ROUTES.GET_ALL + searchName + expired,
    getCommandes,
    {
      onError() {
        setSnack({
          title: "Erreur interne!",
          severityType: "error",
        });
      },
    }
  );
};

export const useGetCommande = (
  id: string | undefined
): UseQueryResult<Commande> => {
  const setSnack = useSnack();

  const getCommande = async (): Promise<Commande> => {
    const CommandeBackend = await getCommandeBackend();
    return await CommandeBackend.getCommande(id);
  };

  return useQueryWithLoading(COMMANDE_ROUTES.GET + id, () => getCommande(), {
    onError() {
      setSnack({
        title: "Erreur interne!",
        severityType: "error",
      });
    },
    enabled: !!id,
  });
};

export const useCreateCommande = (): UseMutationResult<
  Commande,
  Error,
  CreateCommandePayload
> => {
  const setSnack = useSnack();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.user.user);

  const createCommande = async (
    payload: CreateCommandePayload
  ): Promise<Commande> => {
    const CommandeBackend = await getCommandeBackend();
    return await CommandeBackend.createCommande(user.workspaceId, payload);
  };

  return useMutationWithLoading(
    (payload: CreateCommandePayload) => createCommande(payload),
    {
      onSuccess() {
        setSnack({
          title: "Commande créée avec succès!",
          severityType: "success",
        });

        queryClient.refetchQueries(COMMANDE_ROUTES.GET_ALL);

        navigate("/admin/commande");
      },
      onError() {
        setSnack({
          title: "Erreur interne!",
          severityType: "error",
        });
      },
    }
  );
};

export const useUpdateCommande = (
  id: string | undefined
): UseMutationResult<Commande, Error, CreateCommandePayload> => {
  const setSnack = useSnack();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const updateCommande = async (
    payload: CreateCommandePayload
  ): Promise<Commande> => {
    const CommandeBackend = await getCommandeBackend();
    return await CommandeBackend.updateCommande(id, payload);
  };

  return useMutationWithLoading(
    (payload: CreateCommandePayload) => updateCommande(payload),
    {
      onSuccess: (data) => {
        setSnack({
          title: "Commande modifiée avec succès!",
          severityType: "success",
        });

        queryClient.invalidateQueries([COMMANDE_ROUTES.GET, id]);
        queryClient.setQueryData([COMMANDE_ROUTES.GET, id], data);

        navigate("/admin/commande");
      },
      onError() {
        setSnack({
          title: "Erreur interne!",
          severityType: "error",
        });
      },
    }
  );
};

export const useArchiveCommande = (): UseMutationResult<
  SuccessResponse,
  Error,
  string
> => {
  const setSnack = useSnack();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const archiveCommande = async (id: string): Promise<SuccessResponse> => {
    const CommandeBackend = await getCommandeBackend();
    return await CommandeBackend.archiveCommande(id);
  };

  return useMutationWithLoading((id: string) => archiveCommande(id), {
    onSuccess() {
      setSnack({
        title: "Commande supprimée avec succès!",
        severityType: "success",
      });

      queryClient.refetchQueries(COMMANDE_ROUTES.GET_ALL);

      navigate("/admin/commande");
    },
    onError() {
      setSnack({
        title: "Erreur interne!",
        severityType: "error",
      });
    },
  });
};

export const useCreateCommandeFromDevis = () => {
  return useMutation((devisData: CreateCommandePayload) =>
    axios.post("/commande", devisData)
  );
};

interface UploadDocumentPayload {
  commandeId: string;
  file: File;
}

export const useUploadDocument = (
  refetch: () => void
): UseMutationResult<SuccessResponse, Error, UploadDocumentPayload> => {
  const setSnack = useSnack();

  const uploadDocument = async ({
    commandeId,
    file,
  }: UploadDocumentPayload): Promise<SuccessResponse> => {
    const commandeBackend = await getCommandeBackend();
    return await commandeBackend.uploadDocument(commandeId, file);
  };

  return useMutationWithLoading(uploadDocument, {
    onSuccess() {
      setSnack({
        title: "Document téléversé avec succès!",
        severityType: "success",
      });
      refetch();
    },
  });
};

export const useDeleteDocument = (
  refetch: () => void
): UseMutationResult<SuccessResponse, Error, DeleteDocumentPayload> => {
  const setSnack = useSnack();

  const deleteDocument = async (
    payload: DeleteDocumentPayload
  ): Promise<SuccessResponse> => {
    const commandeBackend = await getCommandeBackend();
    return await commandeBackend.deleteDocument(payload);
  };

  return useMutationWithLoading(
    (payload: DeleteDocumentPayload) => deleteDocument(payload),
    {
      onSuccess() {
        setSnack({
          title: "Document supprimé avec succès!",
          severityType: "success",
        });
        refetch();
      },
      onError() {
        setSnack({
          title: "Erreur interne!",
          severityType: "error",
        });
      },
    }
  );
};
